/**
 * Test-hook bridge for the WindowContext.
 *
 * Exposes WindowContext's openWindow / closeWindow / focusWindow on
 * `window.__repancyTestWindow` so Phase J Playwright specs can drive
 * window opens without dblclicking a desktop icon (which gets pointer-
 * intercepted when other windows overlap the icon — surfaced by J
 * first-run attempt on `vault-export-zip.spec.ts`).
 *
 * Gated on `?test=1` — same convention as `test-hooks/index.ts`. Mounts
 * inside `<DesktopInner>` so it reads the live WindowContext via the
 * hook surface (the test-hooks index runs at module init time, BEFORE
 * any provider is mounted, so it can't `useContext` itself).
 *
 * Backlog: F-WINDOW-STACK-TEST-HOOK (Long-tail).
 */

import { useEffect } from 'react';

import { useWindowContext, type WindowContextValue } from '../../components/desktop/WindowContext';

interface TestWindowApi {
  /** Open a registered window by path (e.g. `/obsidian-vault`). */
  openWindow: (path: string) => void;
  /** Close one registered window by path. */
  closeWindow: (path: string) => void;
  /** Focus (bring to front) a window without changing its state. */
  focusWindow: (path: string) => void;
  /**
   * Close every window currently in `open` state. Useful in
   * `beforeEach` to clear the desktop before driving an icon dblclick
   * or `openWindow` for the spec under test.
   */
  closeAll: () => void;
  /** Read the current list of windows (path + state) for spec assertions. */
  list: () => Array<{ path: string; state: string; zIndex: number }>;
}

function shouldInstall(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return new URLSearchParams(window.location.search).get('test') === '1';
  } catch {
    return false;
  }
}

function buildApi(ctx: WindowContextValue): TestWindowApi {
  return {
    openWindow: (path) => ctx.openWindow(path),
    closeWindow: (path) => ctx.closeWindow(path),
    focusWindow: (path) => ctx.focusWindow(path),
    closeAll: () => {
      for (const w of ctx.windows) {
        if (w.state === 'open') ctx.closeWindow(w.path);
      }
    },
    list: () =>
      ctx.windows.map((w) => ({
        path: w.path,
        state: w.state,
        zIndex: w.zIndex,
      })),
  };
}

/**
 * Mount inside `<WindowProvider>` (e.g. `<DesktopInner>`). Reads the
 * live WindowContext and writes a stable proxy onto
 * `window.__repancyTestWindow`. Re-installs on every context change so
 * the API always reflects the current `windows` array.
 *
 * Renders null — pure side-effect component.
 */
export function TestWindowBridge(): null {
  const ctx = useWindowContext();

  useEffect(() => {
    if (!shouldInstall()) return;
    (window as unknown as { __repancyTestWindow?: TestWindowApi }).__repancyTestWindow =
      buildApi(ctx);
  }, [ctx]);

  return null;
}
